import React, { useMemo, useState } from 'react';

/**
 * HfpV2SummaryTable
 * -----------------
 * Compact, collapsible clinician summary of the Holistic Functional Profile (v2),
 * rendered from the deterministic scoring engine output (`domains.hfpV2.scoring`).
 *
 * A fixed-layout, full-width table keeps the Home and School columns in stable
 * positions (not packed at the left, no shifting when a domain is expanded).
 * Each domain header shows an overall Home/School status (the most significant of
 * its subdomains); expanding reveals one row per subdomain with a red / amber /
 * green dot per context.
 *
 * Props:
 *   scoring: the `domains.hfpV2.scoring` object (run_hfp_v2_pipeline output).
 *   defaultExpanded: if true, all domains start expanded (default false = compact).
 */

function ragInfo(classification) {
  const c = (classification || '').toLowerCase();
  if (c.includes('moderate') || c.includes('severe') || c.includes('support') || c.includes('priority')) {
    return { rank: 3, tone: 'bg-red-500', label: classification };
  }
  if (c.includes('mild')) return { rank: 2, tone: 'bg-amber-400', label: 'Mild difficulty' };
  // The engine emits "No difficulty" (CLASSIFICATION_NO_DIFFICULTY); also accept the
  // legacy bare "none"/"no concerns" so genuine no-difficulty renders green, not grey.
  if (c === 'none' || c.includes('no difficulty') || c.includes('no concerns')) {
    return { rank: 1, tone: 'bg-emerald-500', label: 'No difficulty' };
  }
  return { rank: 0, tone: 'bg-gray-300', label: classification || 'Not assessed' };
}

/**
 * Derive a difficulty classification from a subdomain mean using the SAME
 * thresholds every domain uses (and the report page's classifyEnv), so the
 * summary and the report stay consistent.
 */
function classFromMean(mean) {
  if (mean == null || Number.isNaN(Number(mean))) return null;
  const m = Number(mean);
  if (m >= 0) return 'No difficulty';
  if (m >= -1) return 'Mild difficulty';
  return 'Moderate or severe difficulty';
}

/**
 * Resolve the classification string for a subdomain.
 *
 * Environmental Factors and Sensory carry a pointer label
 * ("Environmental (see SAI)" / "Sensory (see profile tags)") instead of a
 * difficulty bucket, but the engine still computes their subdomain means. Per
 * product decision these areas use the SAME RAG and scoring methodology as every
 * other domain, so whenever the engine label isn't a recognised difficulty bucket
 * we derive the classification from the mean. Display-only: this does not change
 * scoring or the engine classification.
 */
function classificationFor(domainName, sideClass, sideSub, sub) {
  const label = sideClass?.[domainName]?.[sub] ?? null;
  if (label && ragInfo(label).rank > 0) return label; // recognised difficulty label
  const mean = sideSub?.[domainName]?.[sub]?.mean;
  if (mean != null && !Number.isNaN(Number(mean))) return classFromMean(mean);
  return label;
}

function Dot({ tone, label }) {
  return <span className={`inline-block w-3 h-3 rounded-full ${tone} ring-1 ring-black/5`} title={label} />;
}

function worst(classifications) {
  let best = null;
  for (const c of classifications) {
    const info = ragInfo(c);
    if (info.rank === 0) continue;
    if (!best || info.rank > best.rank) best = info;
  }
  return best || { tone: 'bg-gray-300', label: 'Not assessed' };
}

export default function HfpV2SummaryTable({ scoring, defaultExpanded = false }) {
  const domains = useMemo(() => {
    const phase1 = scoring?.phase1 || {};
    const homeDomains = phase1.home?.domains || {};
    const homeClass = phase1.home?.classifications?.subdomains || {};
    const schoolClass = phase1.school?.classifications?.subdomains || {};
    const homeSub = phase1.home?.subdomains || {};
    const schoolSub = phase1.school?.subdomains || {};
    return Object.keys(homeDomains).map((name) => {
      const subs = (homeDomains[name]?.subdomains) || [];
      const homeOf = (s) => classificationFor(name, homeClass, homeSub, s);
      const schoolOf = (s) => classificationFor(name, schoolClass, schoolSub, s);
      return {
        name,
        homeWorst: worst(subs.map(homeOf)),
        schoolWorst: worst(subs.map(schoolOf)),
        subdomains: subs.map((s) => ({
          name: s,
          home: ragInfo(homeOf(s)),
          school: ragInfo(schoolOf(s)),
        })),
      };
    });
  }, [scoring]);

  const [expanded, setExpanded] = useState(() => {
    const init = {};
    if (defaultExpanded) domains.forEach((d) => { init[d.name] = true; });
    return init;
  });
  const toggle = (name) => setExpanded((p) => ({ ...p, [name]: !p[name] }));

  if (!scoring || domains.length === 0) {
    return <div className="text-sm text-gray-500 italic">No HFP profile available yet.</div>;
  }

  return (
    <div className="text-sm">
      <table className="table-fixed w-full border-collapse text-left">
        <colgroup>
          <col />
          <col className="w-20" />
          <col className="w-20" />
        </colgroup>
        <thead>
          <tr className="text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">
            <th className="text-left font-semibold py-1 pr-3">Domain</th>
            <th className="text-left font-semibold py-1">Home</th>
            <th className="text-left font-semibold py-1">School</th>
          </tr>
        </thead>
        <tbody>
          {domains.map((d) => {
            const isOpen = !!expanded[d.name];
            return (
              <React.Fragment key={d.name}>
                <tr className="border-b border-gray-100 cursor-pointer hover:bg-gray-50" onClick={() => toggle(d.name)}>
                  <td className="py-1.5 pr-3 align-top">
                    <span className="flex items-start gap-1.5 font-medium text-[#4a2c5a]">
                      <svg className={`w-3 h-3 mt-0.5 flex-shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                      <span>{d.name}</span>
                    </span>
                  </td>
                  <td className="py-1.5 align-top"><Dot tone={d.homeWorst.tone} label={d.homeWorst.label} /></td>
                  <td className="py-1.5 align-top"><Dot tone={d.schoolWorst.tone} label={d.schoolWorst.label} /></td>
                </tr>
                {isOpen && d.subdomains.map((s) => (
                  <tr key={`${d.name}-${s.name}`} className="border-b border-gray-50 text-gray-600">
                    <td className="py-0.5 pl-6 pr-3 align-top">{s.name}</td>
                    <td className="py-0.5 align-top"><Dot tone={s.home.tone} label={s.home.label} /></td>
                    <td className="py-0.5 align-top"><Dot tone={s.school.tone} label={s.school.label} /></td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {/* Legend */}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> No difficulty</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-400" /> Mild</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Moderate/severe</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gray-300" /> Not assessed</span>
      </div>
    </div>
  );
}
